import { ApolloClient, InMemoryCache, createHttpLink, from } from "@apollo/client";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { useCallback, useEffect, useRef, useState } from "react";

import type { AppTypeEnum } from "../../../apiTypes";
import { isWeb } from "../../constants";
import { saveTokens } from "../../helpers/tokens";
import { getAuthLink, refreshAccessToken } from "./authLink";
import { getErrorLink } from "./errorLink";

const removeTypenameLink = removeTypenameFromVariables();

type UseInitApolloClientOptions = {
  url: string;
  skipOnPathname?: boolean;
  appType: AppTypeEnum;
  addSentryBreadcrumb: ToDo;
  captureSentryException: ToDo;
  AppStorage: ToDo;
};

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        availableReservations: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

export const useInitApolloClient = ({
  url: uri,
  skipOnPathname,
  appType,
  addSentryBreadcrumb,
  captureSentryException,
  AppStorage,
}: UseInitApolloClientOptions) => {
  const initialised = useRef(false);
  const [apolloClient, setApolloClient] = useState<ApolloClient<unknown> | null>(null);
  const authLink = getAuthLink({ appType, addSentryBreadcrumb, AppStorage });
  const errorLink = getErrorLink({ addSentryBreadcrumb, captureSentryException, AppStorage });

  // As region from 'expo-localization' may depends on app settings and return incorrect country code,
  // we can extract it based on current domain's tld (web platform)
  // or by making a request to external api (mobile app).
  // Add possibility for user to change it's country code in profile.
  const fetchCountryCode = useCallback(async () => {
    const countryCode = AppStorage().get("countryCode");
    if (countryCode) return;
    const userCountryCode = isWeb
      ? (window.location?.host?.split(".").pop()?.length === 2
          ? window.location.host.split(".").pop()
          : "PL") || "PL"
      : await fetch("https://api.country.is/")
          .then((r) => r.json())
          .then((data) => (data?.country as string) || "PL");

    AppStorage().set("countryCode", userCountryCode.toUpperCase());
  }, [AppStorage]);

  const refreshToken = useCallback(async () => {
    if (initialised.current) return;

    const tokenExpiresIn = AppStorage().get("tokenExpiresIn") as number;
    const isTokenExpired = Date.now() > tokenExpiresIn;

    if (apolloClient && isTokenExpired && skipOnPathname) {
      const accessToken = (await refreshAccessToken({ client: apolloClient, AppStorage })) || "";
      saveTokens({ accessToken, AppStorage });
    }
  }, [AppStorage, apolloClient, skipOnPathname]);

  const initializeApollo = useCallback(async () => {
    const baseLink = from([removeTypenameLink, errorLink, createHttpLink({ uri })]);

    const client = new ApolloClient({
      link: baseLink,
      cache,
      defaultOptions: {
        watchQuery: { fetchPolicy: "cache-and-network", errorPolicy: "all" },
        query: { errorPolicy: "all" },
        mutate: { errorPolicy: "all" },
      },
    });

    const accessToken = (await refreshAccessToken({ client, AppStorage })) || "";
    saveTokens({ accessToken, AppStorage });

    client.setLink(from([removeTypenameLink, errorLink, authLink, createHttpLink({ uri })]));

    setApolloClient(client);
  }, [AppStorage, authLink, errorLink, uri]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    fetchCountryCode();
  }, []);

  useEffect(() => {
    refreshToken();
    // pathname could be removed, but I want refetching check to happen on every page transition
    // ideally the check should happen right before GQL request inside `authLink`, but I have circular dependency issue
  }, [refreshToken]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    initializeApollo();
  }, [uri]);

  return apolloClient;
};
